<template>
  <div
    class="tltp"
    v-if="(isReady || isLoading) && targetEl"
    :style="{
      top: tooltipPosition.top + 'px',
      left: tooltipPosition.left + 'px',
      zIndex: 99999,
      pointerEvents: pointerEventsState
    }"
    @mouseenter="handleTooltipMouseEnter"
    @mouseleave="handleTooltipMouseLeave"
  >
    <div 
      class="tltp__wrapper" 
      :style="{
        opacity: wrapperOpacity,
        transform: wrapperTransform
      }"
      ref="wrapperEl"
    >
      <template v-if="isReady">
        <div class="tltp__body">
          <AvatarImage class="tltp__avatar" :src="data.image" :size="24" />
          <div class="tltp__hdr">
            <span class="tltp__name">{{ data.symbol }}</span>
            <span class="tltp__price">${{ data.price_usd }}</span>
            <span
              class="tltp__percent"
              :class="data.change_1d_usd < 0 ? 'tltp__percent--red' : 'tltp__percent--green'"
            >
              {{ data.change_1d_usd }}%
            </span>
            <a class="tltp__drops-icon" :href="data.dropstab_url">
              <DropsIcon />
            </a>
            <button class="tltp__forecaster-icon" @click="goToForecasters()">
              <ForecasterIcon />
            </button>
          </div>
          <div class="tltp__footer">
            <p class="tltp__footer-item">
              <span class="tltp__footer-prefix">#</span> {{ data.rank }}
            </p>
            <p class="tltp__footer-item">
              <span class="tltp__footer-prefix">MC</span> {{ data.market_cap }}
            </p>
            <p class="tltp__footer-item">
              <span class="tltp__footer-prefix">FDV</span> ${{ data.fdv }}
            </p>
          </div>
        </div>
      </template>
      <template v-else-if="isLoading">
        <div class="tltp__loader">
          <StubsBlock />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
  import { useStore } from 'vuex';
  import { throttle } from 'lodash';
  import { useRouter } from 'vue-router';
  import { PATHS_NAME } from '@shared/routers/consts/pathsName';
  import { closeContentPopup } from '@/Groups/CoinTooltip/private/imports/closeModalContent';
  import { callForecasterCoinFilter } from '@/Groups/Forecasters/public/utils/activeForecasterFilter';
  //----------------------------------------
  import { AvatarImage } from '@/Groups/CoinTooltip/private/imports/externalTemplates';
  import ForecasterIcon from '@/Groups/CoinTooltip/private/svg/ForecasterIcon.vue';
  import DropsIcon from '@/Groups/CoinTooltip/private/svg/DropsIcon.vue';
  import StubsBlock from '@/Groups/CoinTooltip/private/ui/StubsBlock/StubsBlock.vue';

  // =============================================
  // КОНФИГУРАЦИЯ АНИМАЦИИ
  // =============================================
  const ANIMATION_CONFIG = {
    // Длительность анимации в ms
    DURATION: 100,
    // Смещение по Y в px 
    TRANSLATE_OFFSET: 10,
    // Задержка перед закрытием при уходе мыши
    MOUSE_LEAVE_DELAY: 50,
    // Задержка перед полным закрытием
    CLOSE_DELAY: 150,
    // Задержка для throttle
    THROTTLE_DELAY: 250
  };

  // =============================================
  // ИНИЦИАЛИЗАЦИЯ СТОРА И РОУТЕРА
  // =============================================
  const store = useStore();
  const router = useRouter();

  // =============================================
  // РЕАКТИВНЫЕ ПЕРЕМЕННЫЕ
  // =============================================
  const tooltipPosition = ref({ top: 0, left: 0 });
  const targetEl = ref(null);
  const isHoveringTooltip = ref(false);
  const wrapperEl = ref(null);
  const wrapperOpacity = ref(0);
  const wrapperTransform = ref(`translateY(${ANIMATION_CONFIG.TRANSLATE_OFFSET}px)`);
  const pointerEventsState = ref('none');
  
  let animationFrameId = null;
  let tooltipHeight = 0;

  // =============================================
  // COMPUTED СВОЙСТВА ИЗ VUEX
  // =============================================
  const isOpen = computed(() => store.getters['CoinTooltip/getIsOpen']);
  const isLoading = computed(() => store.getters['CoinTooltip/getIsLoading']);
  const isReady = computed(() => store.getters['CoinTooltip/getIsReady']);
  const data = computed(() => store.getters['CoinTooltip/getData']);

  // =============================================
  // ФУНКЦИИ АНИМАЦИИ
  // =============================================

  //Парсит значение translateY из строки transform
  const parseTransformY = (transform) => {
    const match = transform.match(/translateY\(([^)]+)px\)/);
    return match ? parseFloat(match[1]) : 0;
  };

  //Основная функция анимации opacity и transform
  const animateTooltip = (targetOpacity, targetTransform, duration = ANIMATION_CONFIG.DURATION) => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }

    const startOpacity = wrapperOpacity.value;
    const startTransform = wrapperTransform.value;
    
    const startY = parseTransformY(startTransform);
    const targetY = parseTransformY(targetTransform);
    
    const startTime = performance.now();

    pointerEventsState.value = 'none';

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeProgress = progress < 0.5 
        ? 2 * progress * progress 
        : -1 + (4 - 2 * progress) * progress;

      wrapperOpacity.value = startOpacity + (targetOpacity - startOpacity) * easeProgress;

      const currentY = startY + (targetY - startY) * easeProgress;
      wrapperTransform.value = `translateY(${currentY}px)`;

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        wrapperOpacity.value = targetOpacity;
        wrapperTransform.value = targetTransform;
        animationFrameId = null;
        
        if (targetOpacity === 1) {
          pointerEventsState.value = 'auto';
        }
      }
    };

    animationFrameId = requestAnimationFrame(animate);
  };

  //Мгновенное скрытие (без анимации)
  const hideTooltipInstantly = () => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    wrapperOpacity.value = 0;
    wrapperTransform.value = `translateY(${ANIMATION_CONFIG.TRANSLATE_OFFSET}px)`;
    pointerEventsState.value = 'none';
  };

  //Плавное открытие тултипа
  const showTooltipWithAnimation = () => {
    animateTooltip(1, 'translateY(0px)');
  };

  //Плавное закрытие тултипа
  const hideTooltipWithAnimation = () => {
    pointerEventsState.value = 'none';
    animateTooltip(0, `translateY(${ANIMATION_CONFIG.TRANSLATE_OFFSET}px)`);
  };

  // =============================================
  // ФУНКЦИИ ПОЗИЦИОНИРОВАНИЯ
  // =============================================

  //Находит элемент под курсором для показа тултипа
  const tooltipElement = (e) => {
    return (
      e.target.closest('.title-tooltip-coin') ||
      e.target.closest('.Coin-Info-Tooltip') ||
      e.target.closest('[data-coin]')
    );
  };

  //Обновляет позицию тултипа относительно целевого элемента
  const updateTooltipPosition = async (el) => {
    if (!el) return;
    await nextTick();
    const targetRect = el.getBoundingClientRect();
    const tooltipEl = document.querySelector('.tltp__wrapper');
    const tooltipWidth = tooltipEl?.offsetWidth || 220;
    tooltipHeight = tooltipEl?.offsetHeight || 100;
    const margin = 8;
    const viewportWidth = window.innerWidth;
    
    let top = targetRect.top + window.scrollY - tooltipHeight - margin;
    let left = targetRect.left + window.scrollX + targetRect.width / 2 - tooltipWidth / 2;
    
    if (top < window.scrollY) {
      top = targetRect.bottom + window.scrollY + margin;
    }
    if (left < 0) left = margin;
    if (left + tooltipWidth > viewportWidth) left = viewportWidth - tooltipWidth - margin;
    
    tooltipPosition.value = { top, left };
  };

  // =============================================
  // ФУНКЦИИ УПРАВЛЕНИЯ ТУЛТИПОМ
  // =============================================

  //Вызывает показ тултипа для элемента
  const callTooltip = async (e) => {
    const el = tooltipElement(e);
    if (!el) return;
    if (targetEl.value === el) return;
    
    targetEl.value = el;
    const dataCoin = el.getAttribute('data-coin');
    if (!dataCoin) return;
    
    await updateTooltipPosition(el);
    store.dispatch('CoinTooltip/toggleOpenedFlag', true);
    store.dispatch('CoinTooltip/init', dataCoin);
  };

  //Полностью закрывает тултип
  const closeTooltip = () => {
    if (isHoveringTooltip.value) return;
    
    store.dispatch('CoinTooltip/closeTooltip');
    targetEl.value = null;
  };

  // =============================================
  // ОБРАБОТЧИКИ СОБЫТИЙ МЫШИ
  // =============================================

  //Обработчик наведения на тултип
  const handleTooltipMouseEnter = () => {
    isHoveringTooltip.value = true;
  };

  //Обработчик ухода с тултипа
  const handleTooltipMouseLeave = () => {
    isHoveringTooltip.value = false;
    hideTooltipWithAnimation();
    setTimeout(() => {
      if (!isHoveringTooltip.value) {
        closeTooltip();
      }
    }, ANIMATION_CONFIG.MOUSE_LEAVE_DELAY);
  };

  //Проверяет положение курсора и управляет тултипом
  const checkCursorOnTicker = (e) => {
    const el = tooltipElement(e);
    if (!el) {
      const isOnTooltip = e.target.closest('.tltp');
      if (!isOnTooltip) {
        hideTooltipWithAnimation();
        setTimeout(() => {
          closeTooltip();
        }, ANIMATION_CONFIG.CLOSE_DELAY);
      }
      return;
    }
    callTooltip(e);
  };

  //Обработчик клика по элементу с тултипом
  const clickCoin = (e) => {
    if (!tooltipElement(e)) return;
    e.preventDefault();
    callTooltip(e);
  };

  //Обработчик mouseout документа
  const handleDocumentMouseOut = (e) => {
    if (!e.relatedTarget) {
      hideTooltipWithAnimation();
      setTimeout(() => {
        closeTooltip();
      }, ANIMATION_CONFIG.CLOSE_DELAY);
      return;
    }
    
    const fromEl = e.target.closest('[data-coin], .title-tooltip-coin, .Coin-Info-Tooltip');
    if (!fromEl) return;

    const toEl = e.relatedTarget.closest('[data-coin], .title-tooltip-coin, .Coin-Info-Tooltip, .tltp');
    if (toEl) return;

    hideTooltipWithAnimation();
    setTimeout(() => {
      closeTooltip();
    }, ANIMATION_CONFIG.CLOSE_DELAY);
  };

  //Обработчик mouseleave документа
  const handleDocumentMouseLeave = (e) => {
    if (!e.relatedTarget) {
      hideTooltipWithAnimation();
      setTimeout(() => {
        closeTooltip();
      }, ANIMATION_CONFIG.CLOSE_DELAY);
    }
  };

  // =============================================
  // ФУНКЦИИ НАВИГАЦИИ
  // =============================================

  //Переход к странице форкастеров
  const goToForecasters = () => {
    const coin = {
      api_id: `${data.value.id}`,
      image: data.value.image,
      name: data.value.name,
      symbol: data.value.symbol,
      dropstab_url: data.value.dropstab_url,
    }
    router.push({ name: PATHS_NAME.FORECASTERS });
    closeContentPopup();
    callForecasterCoinFilter(coin);
  }

  // =============================================
  // LIFECYCLE ХУКИ
  // =============================================
  onMounted(() => {
    document.addEventListener('mousemove', throttledMouseMove);
    document.addEventListener('click', clickCoin);
    document.addEventListener('mouseout', handleDocumentMouseOut);
    document.addEventListener('mouseleave', handleDocumentMouseLeave);
  });

  onUnmounted(() => {
    document.removeEventListener('mousemove', throttledMouseMove);
    document.removeEventListener('click', clickCoin);
    document.removeEventListener('mouseout', handleDocumentMouseOut);
    document.removeEventListener('mouseleave', handleDocumentMouseLeave);

    if (throttledMouseMove && throttledMouseMove.cancel) throttledMouseMove.cancel();
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
  });

  // =============================================
  // WATCHERS
  // =============================================
  watch([isReady, isLoading], async () => {
    if ((isReady.value || isLoading.value) && targetEl.value) {
      await nextTick();
      updateTooltipPosition(targetEl.value);
    }
  });

  watch(isOpen, (newVal) => {
    if (newVal) {
      showTooltipWithAnimation();
    } else {
      if (!isHoveringTooltip.value) {
        hideTooltipWithAnimation();
      }
    }
  }, { immediate: true });

  // =============================================
  // THROTTLE ФУНКЦИИ
  // =============================================
  const throttledMouseMove = throttle(checkCursorOnTicker, ANIMATION_CONFIG.THROTTLE_DELAY);
</script>

<style lang="scss" scoped>
  @import "./styles.scss";
</style>